import { GLOBAL_SEARCH_TABS } from "../constants/global-search-tabs";
import { SETTINGS_SECTION_VALUES } from "../constants/navigation-tabs";
import {
  EGlobalSearchItemType,
  type IGlobalSearchGroup,
  type IGlobalSearchItem,
} from "../types/global-search";
import {
  EGlobalSearchTab,
  type IFilterGlobalSearchGroupsByTabProps,
  type IGetAdjacentGlobalSearchTabProps,
  type IGetGlobalSearchTabsProps,
  type IGlobalSearchTabDefinition,
  type IIsGlobalSearchItemInTabProps,
  type IIsGlobalSearchTabShowingTypeProps,
} from "../types/global-search-tabs";

// NOTE: The types whose items are split between SETTINGS and
// ORGANIZATION_SETTINGS by the settings area they open.
const SETTINGS_AREA_TYPES: ReadonlySet<EGlobalSearchItemType> = new Set([
  EGlobalSearchItemType.SETTINGS_TAB,
  EGlobalSearchItemType.SETTINGS_OPTION,
  EGlobalSearchItemType.API_KEY,
]);

// NOTE: An API key opens where its scope lives: a personal key in the
// profile's API Keys tab, an organization or Syncator key in the
// organization's settings (getGlobalSearchItemTarget).
const PERSONAL_API_KEY_SCOPE = "personal";

const isOrganizationAreaItem = (item: IGlobalSearchItem): boolean =>
  item.type === EGlobalSearchItemType.API_KEY
    ? item.params.scope !== PERSONAL_API_KEY_SCOPE
    : item.params.section === SETTINGS_SECTION_VALUES.ORGANIZATION;

const TAB_DEFINITIONS: ReadonlyMap<
  EGlobalSearchTab,
  IGlobalSearchTabDefinition
> = new Map(
  GLOBAL_SEARCH_TABS.map((definition) => [definition.tab, definition]),
);

export const getGlobalSearchTabDefinition = (
  tab: EGlobalSearchTab,
): IGlobalSearchTabDefinition =>
  TAB_DEFINITIONS.get(tab) ?? GLOBAL_SEARCH_TABS[0];

// NOTE: NSAI-6853 — the tabs this user is offered, in display order. ALL has
// no gates, so the list is never empty.
export const getGlobalSearchTabs = ({
  isGateOpen,
}: IGetGlobalSearchTabsProps): IGlobalSearchTabDefinition[] =>
  GLOBAL_SEARCH_TABS.filter((definition) =>
    definition.gates.every((gate) => isGateOpen(gate)),
  );

// NOTE: Whether a tab keeps items of this type at all. ALL keeps every type;
// the settings split is per item, so both settings tabs answer true here.
export const isGlobalSearchTabShowingType = ({
  tab,
  type,
}: IIsGlobalSearchTabShowingTypeProps): boolean =>
  tab === EGlobalSearchTab.ALL ||
  getGlobalSearchTabDefinition(tab).types.includes(type);

export const isGlobalSearchItemInTab = ({
  tab,
  item,
}: IIsGlobalSearchItemInTabProps): boolean => {
  if (tab === EGlobalSearchTab.ALL) return true;
  if (!isGlobalSearchTabShowingType({ tab, type: item.type })) return false;
  if (!SETTINGS_AREA_TYPES.has(item.type)) return true;

  const isOrganizationArea = isOrganizationAreaItem(item);
  return tab === EGlobalSearchTab.ORGANIZATION_SETTINGS
    ? isOrganizationArea
    : !isOrganizationArea;
};

// NOTE: The groups of a tab, each keeping only the items that belong to it;
// groups left empty are dropped.
export const filterGlobalSearchGroupsByTab = ({
  tab,
  groups,
}: IFilterGlobalSearchGroupsByTabProps): IGlobalSearchGroup[] => {
  if (tab === EGlobalSearchTab.ALL) return [...groups];

  return groups
    .map((group) => ({
      type: group.type,
      items: group.items.filter((item) =>
        isGlobalSearchItemInTab({ tab, item }),
      ),
    }))
    .filter((group) => group.items.length > 0);
};

// NOTE: The tab ←/→ (or a swipe) moves to, wrapping at both ends. A tab that
// is no longer offered starts from ALL.
export const getAdjacentGlobalSearchTab = ({
  tabs,
  tab,
  step,
}: IGetAdjacentGlobalSearchTabProps): EGlobalSearchTab => {
  if (tabs.length === 0) return EGlobalSearchTab.ALL;
  const index = Math.max(
    0,
    tabs.findIndex((definition) => definition.tab === tab),
  );
  const nextIndex = (index + step + tabs.length) % tabs.length;
  return tabs[nextIndex].tab;
};
